import { defineComponent, onBeforeUnmount, onMounted, reactive, ref, toRefs } from 'vue';
import EventBus from '../../scripts/lib/EventBus.js';
import {
  type ColorItem,
  observeAndTrackColorSelectionContainer,
  previewDifferentColor,
  releasePreviewDifferentColor,
  trackColorChange,
} from '../../scripts/colorHelper.js';
import { mediaQuery } from '../../scripts/lib/mediaQuery.js';

const colorThumbnailTemplate = document.getElementById('color-thumbnails-list-template');

function resetItemsHiddenClasses(allItems: NodeListOf<Element> | Element[]) {
  allItems.forEach((item) => {
    item.classList.remove('ents-colors-thumbnails--list-item--hidden');
    (item.querySelector('.ents-colors-thumbnails--list__tooltip-trigger') as Element).classList.remove(
      'ents-colors-thumbnails--list__tooltip-trigger--hidden',
    );
  });
}

function addItemsHiddenClass(itemsToHide: Element[]) {
  itemsToHide.forEach((item) => {
    item.classList.add('ents-colors-thumbnails--list-item--hidden');
  });
}

export default defineComponent({
  template: colorThumbnailTemplate ? colorThumbnailTemplate.innerHTML : '',

  props: {
    colors: {
      type: Array,
      required: true,
    },
    syan: {
      type: Number,
      required: true,
    },
    getArticleForColor: {
      type: Function,
      required: false,
    },
  },

  setup(props) {
    const state = reactive<{
      colorTracking: Record<string, unknown> | null;
      checkIfTouchDevice: boolean;
      listOpened: boolean;
    }>({
      colorTracking: null,
      checkIfTouchDevice: mediaQuery.checkIfTouchDevice(),
      listOpened: false,
    });

    const colorSelectionObserver = ref<ReturnType<typeof observeAndTrackColorSelectionContainer> | null>(null);
    const moreButtonSelector = '.ents-colors-thumbnails--list-more-button';
    const colorButtonListItemSelector = '.ents-colors-thumbnails--list-item';

    onMounted(() => {
      const thumbnailContainer = document.querySelector('.ents-colors-thumbnails--list');
      if (thumbnailContainer === null) {
        return;
      }
      thumbnailContainer.classList.remove('ents-colors-thumbnails--list-pre-javascript');

      const resizeObserver = new ResizeObserver((entries) => {
        if (entries !== undefined && entries.length > 0) {
          renderColorList(entries[0].target);
        }
      });
      resizeObserver.observe(thumbnailContainer);
      renderColorList(thumbnailContainer);

      initializeColorTrackingObserver();
      window.addEventListener('pageshow', resetTrackingInObserver);

      state.colorTracking = (document.querySelector('.ents-colors__tracking') as HTMLElement).dataset
        .entsColorsTrackingJsonContent as unknown as Record<string, unknown>;
    });

    onBeforeUnmount(() => {
      window.removeEventListener('pageshow', resetTrackingInObserver);
    });

    function resetTrackingInObserver(e: PageTransitionEvent) {
      if (e.persisted && colorSelectionObserver.value) {
        colorSelectionObserver.value.stop();
        initializeColorTrackingObserver();
      }
    }

    function initializeColorTrackingObserver() {
      const thumbnailContainer = document.querySelector('.ents-colors-thumbnails--list');
      if (thumbnailContainer === null) {
        return;
      }
      colorSelectionObserver.value = observeAndTrackColorSelectionContainer(
        thumbnailContainer,
        props.colors as ColorItem[],
        props.syan,
      );
    }

    function isRenderSingleRowOverflowCase(colorsDisplayed: number, thumbnailsFitting: number) {
      return colorsDisplayed > thumbnailsFitting && colorsDisplayed <= thumbnailsFitting * 2 && !state.listOpened;
    }

    function isRenderDoubleRowOverflowCase(colorsDisplayed: number, thumbnailsFitting: number) {
      return colorsDisplayed > thumbnailsFitting * 2 && !state.listOpened;
    }

    function renderMoreButtonOverItem(itemToOverlay: Element, hiddenItemsCounter: number) {
      const moreButton = document.querySelector(moreButtonSelector) as HTMLElement;
      moreButton.classList.add('ents-colors-thumbnails--list-more-button--show');
      moreButton.innerHTML = `+${hiddenItemsCounter}`;
      moreButton.removeAttribute('hidden');
      const radioButtonId = moreButton.getAttribute('for') as string;
      (document.getElementById(radioButtonId) as HTMLElement).removeAttribute('hidden');
      itemToOverlay.appendChild(moreButton);
      (itemToOverlay.querySelector('.ents-colors-thumbnails--list__tooltip-trigger') as Element).classList.add(
        'ents-colors-thumbnails--list__tooltip-trigger--hidden',
      );
    }

    function renderColorList(thumbnailContainer: Element | undefined) {
      if (thumbnailContainer === undefined) {
        return;
      }

      const containerBoundingRect = thumbnailContainer.getBoundingClientRect();
      if (containerBoundingRect.height === 0) {
        return;
      }

      const imageWidth = 60;
      const colGap = 6;
      const colorsDisplayed = (props.colors as unknown[]).length;
      const containerWidth = containerBoundingRect.width;

      // calculate how many items fit horizontally
      const thumbnailsFitting = Math.floor((containerWidth + colGap) / (imageWidth + colGap));
      if (isRenderSingleRowOverflowCase(colorsDisplayed, thumbnailsFitting)) {
        const allItems = document.querySelectorAll(colorButtonListItemSelector);
        resetItemsHiddenClasses(allItems);
        const moreButtonIndex = thumbnailsFitting - 1;
        const itemToOverlay = allItems[moreButtonIndex];
        if (itemToOverlay) {
          renderMoreButtonOverItem(itemToOverlay, colorsDisplayed - thumbnailsFitting + 1);
          addItemsHiddenClass([...allItems].slice(moreButtonIndex + 1));
        }
      } else if (isRenderDoubleRowOverflowCase(colorsDisplayed, thumbnailsFitting)) {
        const allItems = document.querySelectorAll(colorButtonListItemSelector);
        resetItemsHiddenClasses(allItems);
        const moreButtonIndex = thumbnailsFitting * 2 - 1;
        const itemToOverlay = document.querySelectorAll(colorButtonListItemSelector)[moreButtonIndex];
        if (itemToOverlay) {
          renderMoreButtonOverItem(itemToOverlay, colorsDisplayed - thumbnailsFitting * 2 + 1);
          addItemsHiddenClass([...allItems].slice(moreButtonIndex + 1));
        }
      } else {
        // render single row without any overflowing items
        const moreButton = document.querySelector(moreButtonSelector);
        if (!moreButton) {
          return;
        }
        moreButton.classList.remove('ents-colors-thumbnails--list-more-button--show');
        moreButton.setAttribute('hidden', 'true');
        resetItemsHiddenClasses(document.querySelectorAll(colorButtonListItemSelector));
      }
    }

    function selectColor(this: { colorTracking: Record<string, unknown> | null }, articleId: string) {
      releasePreviewDifferentColor();
      EventBus.emit('ents:vue:color:changed', articleId);
      trackColorChange((this.colorTracking as Record<string, unknown>)[articleId]);
    }

    function openList(isKeyboard: boolean) {
      state.listOpened = true;
      const showMoreButton = document.querySelector(moreButtonSelector) as HTMLElement;

      showMoreButton.classList.add('ents-colors-thumbnails--list-more-button--hidden');
      resetItemsHiddenClasses(document.querySelectorAll(colorButtonListItemSelector));

      showMoreButton.classList.add('ents-colors-thumbnails--list-more-button--hidden');
      if (isKeyboard) {
        const showMoreButtonInput = (
          showMoreButton.closest('.ents-colors-thumbnails--list-item') as HTMLElement
        ).querySelector('input') as HTMLInputElement;
        showMoreButtonInput.click();
        showMoreButtonInput.focus();
      }
      (document.querySelector('#ents-color-showAll') as Element).remove();
    }

    return {
      ...toRefs(state),
      previewDifferentColor,
      releasePreviewDifferentColor,
      selectColor,
      openList,
    };
  },
});
